import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
const privacyContent = `
Política de Privacidad

Última actualización: febrero 2026

1. Información que recopilamos
Recopilamos la información que nos proporcionas directamente al realizar una compra, como tu nombre y dirección de correo electrónico.

2. Uso de la información
Utilizamos la información recopilada para:
• Procesar tu pedido y entregarte el contenido adquirido.
• Enviarte comunicaciones relacionadas con tu compra.
• Mejorar nuestros productos y servicios.

3. Protección de datos
Implementamos medidas de seguridad técnicas y organizativas para proteger tu información personal contra acceso no autorizado, alteración o destrucción.

4. Cookies
Nuestro sitio puede utilizar cookies para mejorar la experiencia del usuario. Puedes configurar tu navegador para rechazar cookies, aunque esto podría afectar la funcionalidad del sitio.

5. Terceros
No vendemos, intercambiamos ni transferimos tu información personal a terceros sin tu consentimiento, excepto cuando sea necesario para cumplir con la ley.

6. Tus derechos
Tienes derecho a acceder, rectificar o eliminar tus datos personales. Para ejercer estos derechos, puedes ponerte en contacto con nosotros.

7. Cambios en esta política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Términos y Condiciones

Última actualización: febrero 2026

1. Aceptación de los términos
Al acceder y utilizar este sitio web y adquirir nuestros productos, aceptas estos términos y condiciones en su totalidad.

2. Descripción del producto
"Cero Atracones Nocturnos" es un programa digital de 14 días que incluye guías, plantillas y herramientas descargables. Este producto es de carácter educativo e informativo.

3. Descargo de responsabilidad médica
Este programa NO sustituye el consejo médico, diagnóstico o tratamiento profesional. Si tienes un trastorno alimentario diagnosticado o sospechas que puedes tenerlo, consulta con un profesional de la salud antes de utilizar este programa.

4. Propiedad intelectual
Todo el contenido del programa está protegido por derechos de autor. No está permitida su reproducción, distribución o reventa sin autorización expresa.

5. Política de reembolso
Debido a la naturaleza digital del producto, no se ofrecen reembolsos una vez entregado el contenido. Si tienes problemas con la descarga, nos comprometemos a resolverlos.

6. Limitación de responsabilidad
No garantizamos resultados específicos. Los resultados dependen del compromiso individual de cada usuario con el programa.

7. Modificaciones
Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado del sitio implica la aceptación de los términos actualizados.
`.trim();
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  content
}: LegalModalProps) {
  return <AnimatePresence data-id="element-36">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" data-id="element-37" />
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[80vh] bg-[#111827] border border-gray-700 rounded-2xl z-50 flex flex-col shadow-2xl" data-id="element-38">
            <div className="flex items-center justify-between p-6 border-b border-gray-700/50" data-id="element-39">
              <h2 className="text-xl font-bold text-white" data-id="element-40">{title}</h2>
              <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-800" data-id="element-41">
                <X className="w-5 h-5" data-id="element-42" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1" data-id="element-43">
              <div className="text-gray-300 text-sm leading-[1.8] whitespace-pre-line" data-id="element-44">
                {content}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  return <>
      <footer className="py-8 px-6 bg-[#05070d] border-t border-gray-800/50" data-id="element-45">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-46">
          <p className="text-gray-600 text-sm" data-id="element-47">
            © {new Date().getFullYear()} Cero Atracones Nocturnos. Todos los
            derechos reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-48">
            <button onClick={() => setActiveModal('privacy')} className="text-gray-500 hover:text-amber-400 text-sm transition-colors" data-id="element-49">
              Política de Privacidad
            </button>
            <span className="text-gray-700" data-id="element-50">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-gray-500 hover:text-amber-400 text-sm transition-colors" data-id="element-51">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" content={privacyContent} data-id="element-52" />
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" content={termsContent} data-id="element-53" />
    </>;
}